import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';

import useTenantState from '@/states/tenant.state';
import useJobDescriptionState, { IJobDescription } from '@/states/job.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';

import CompanyHeader from '@/components/company/CompanyHeader';
import ReportFilters from '@/components/company/report/ReportFilters';
import ReportTable from '@/components/company/report/ReportTable';
import ReportForm from '@/components/company/report/ReportForm';

export default function Report() {
  const { currentTenant } = useTenantState();
  const {
    paginatedData,
    updateJobDescription,
    addReportFile,
    getJobDescriptions,
    getJobDescriptionsFull,
    jobDescriptionsFull,
    isLoading,
  } = useJobDescriptionState();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');
  const [formMode, setFormMode] = useState<'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<IJobDescription | null>(null);

  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'name',
    order: 'asc',
  });

  const formControl = useForm<IJobDescription>();
  const { handleSubmit, reset } = formControl;

  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      await getJobDescriptions(currentTenant.id, {
        page: pageConfig.page,
        page_size: pageConfig.limit,
        org_chart_node_id: selectedRole ? Number(selectedRole) : undefined,
        org_key_activity_id: selectedActivity ? Number(selectedActivity) : undefined,
        name: searchTerm || undefined,
        orderBy: pageConfig.orderBy,
        order: pageConfig.order,
      });
    } catch {
      toast.error('Failed to fetch data');
    }
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.orderBy,
    pageConfig.order,
    selectedRole,
    selectedActivity,
    searchTerm,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const displayData = paginatedData || {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 10,
    number: 0,
  };

  const handleEdit = (row: IJobDescription) => {
    let parsedFile = null;
    try {
      const files = row.report_file ? JSON.parse(row.report_file) : [];
      parsedFile = files.length > 0 ? files[0] : null;
    } catch {
      parsedFile = null;
    }
    reset({ ...row, report_file: parsedFile });
    setSelectedRow(row);
    setFormMode('edit');
  };

  const handleSave = async (data: IJobDescription) => {
    if (!currentTenant?.id || !selectedRow?.id) return;
    try {
      let formattedReportFile = selectedRow.report_file ?? null;

      if (data.report_file instanceof File) {
        const uploadRes = await addReportFile(currentTenant.id, selectedRow.id, data.report_file);
        formattedReportFile = JSON.stringify([uploadRes]);
      }

      await updateJobDescription(currentTenant.id, selectedRow.id, {
        org_chart_node_id: selectedRow.org_chart_node_id,
        org_key_activity_id: selectedRow.org_key_activity_id,
        name: selectedRow.name,
        report: data.report ?? '',
        frequency: data.frequency ?? '',
        kpi: selectedRow.kpi ?? '',
        kpi_measurement: selectedRow.kpi_measurement ?? '',
        is_published: selectedRow.is_published ?? false,
        report_file: formattedReportFile,
      });

      toast.success('Report updated successfully');
      setFormMode(null);
      setSelectedRow(null);
      await fetchData();
    } catch {
      toast.error('Failed to update report');
    }
  };

  useEffect(() => {
    if (currentTenant?.id) {
      getJobDescriptionsFull(currentTenant.id);
    }
  }, [currentTenant?.id]);

  return (
    <section className="w-full">
      <CompanyHeader
        title="Report Templates"
        description="Manage reporting structures and templates for different roles."
      />
      <div className="mx-5 border-b border-gray-200" />

      {formMode === 'edit' && selectedRow && (
        <FormProvider {...formControl}>
          <ReportForm
            formControl={formControl}
            onSubmit={handleSubmit(handleSave)}
            onCancel={() => {
              setFormMode(null);
              setSelectedRow(null);
            }}
          />
        </FormProvider>
      )}

      <ReportFilters
        jobDescriptions={jobDescriptionsFull}
        searchTerm={searchTerm}
        selectedRole={selectedRole}
        selectedActivity={selectedActivity}
        onSearch={val => {
          setSearchTerm(val);
          setPageConfig(prev => ({ ...prev, page: 0 }));
        }}
        onRoleChange={val => {
          setSelectedRole(val);
          setPageConfig(prev => ({ ...prev, page: 0 }));
        }}
        onActivityChange={val => {
          setSelectedActivity(val);
          setPageConfig(prev => ({ ...prev, page: 0 }));
        }}
        onClear={() => {
          setSearchTerm('');
          setSelectedRole('');
          setSelectedActivity('');
          setPageConfig(prev => ({ ...prev, page: 0 }));
        }}
      />

      <ReportTable
        isLoading={isLoading}
        paginatedData={displayData}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        onEdit={handleEdit}
      />
    </section>
  );
}
